import { getAppCopy } from '../../../i18n';
import type { EntitlementStatus } from '../types';
import {
  getBillingApiBase,
  getBillingFetchCredentials,
  getOrCreateDeviceId,
  loadBillingConfig,
} from '../billingApi';
import { normalizeRedeemCodeInput } from '../redeemCodeFormat';

interface StatusResponse {
  tier?: 'free' | 'pro';
  source?: EntitlementStatus['source'];
  expiresAt?: string;
  formattedPrice?: string;
  error?: string;
}

async function postJson<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const deviceId = getOrCreateDeviceId();
  const response = await fetch(`${getBillingApiBase()}/${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Pouches-Device-Id': deviceId,
    },
    credentials: getBillingFetchCredentials(),
    body: JSON.stringify({ ...body, device_id: deviceId }),
  });
  const data = (await response.json().catch(() => ({}))) as T & { error?: string };
  if (!response.ok) {
    const billingErrors = getAppCopy().billingErrors;
    throw new Error((data as { error?: string }).error || billingErrors.billingApiError(response.status));
  }
  return data;
}

function mapStatusResponse(data: StatusResponse): EntitlementStatus {
  const configPromise = loadBillingConfig();
  return {
    tier: data.tier === 'pro' ? 'pro' : 'free',
    source:
      data.source === 'appstore' || data.source === 'microsoft'
        ? data.source
        : 'cache',
    expiresAt: data.expiresAt,
    formattedPrice: data.formattedPrice,
  };
}

export async function fetchWebRedeemEntitlement(): Promise<EntitlementStatus> {
  const deviceId = getOrCreateDeviceId();
  const response = await fetch(
    `${getBillingApiBase()}/status.php?device_id=${encodeURIComponent(deviceId)}`,
    { credentials: getBillingFetchCredentials(), cache: 'no-store' },
  );
  const data = (await response.json().catch(() => ({}))) as StatusResponse;
  if (!response.ok) {
    const billingErrors = getAppCopy().billingErrors;
    throw new Error(data.error || billingErrors.statusCheckFailed(response.status));
  }
  const status = mapStatusResponse(data);
  const config = await loadBillingConfig();
  if (!status.formattedPrice) {
    status.formattedPrice = config.monthlyPriceLabel;
  }
  return status;
}

export async function redeemWebCode(rawCode: string): Promise<EntitlementStatus> {
  const code = normalizeRedeemCodeInput(rawCode);
  if (!code) {
    throw new Error(getAppCopy().billingErrors.redeemInvalidCode);
  }
  const data = await postJson<StatusResponse>('redeem-code.php', { code });
  const status = mapStatusResponse(data);
  const config = await loadBillingConfig();
  if (!status.formattedPrice) {
    status.formattedPrice = config.monthlyPriceLabel;
  }
  return status;
}
